/**
 * Consent requirements: what each client is asked, in the order they see it.
 *
 * A requirement starts as a draft, so it can be set up and checked before any
 * client is asked. Activating it is the moment it starts to apply — a mandatory
 * one then keeps every client it applies to on the consent screen until they
 * accept — so that step is confirmed, and says so. Retiring stops asking; every
 * decision already made stays on record.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CheckCircle2, ListChecks, Pencil, Plus } from 'lucide-react';
import {
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  Confirm,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusChip,
  formatDateTime,
  formatNumber,
  toast,
  api,
} from '@connect/web-shared';

import { RequirementFormDialog } from './requirement-form-dialog';
import {
  STATUS_TONES,
  fetchDocuments,
  fetchOptions,
  fetchRequirements,
  labelOf,
  legalKeys,
  type RequirementRow,
} from './types';

type Pending = { row: RequirementRow; action: 'activate' | 'retire' };

export function ConsentRequirementsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<RequirementRow | 'new' | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);

  const options = useQuery({ queryKey: legalKeys.options, queryFn: fetchOptions, staleTime: Infinity });
  const documents = useQuery({ queryKey: legalKeys.documents, queryFn: fetchDocuments });
  const requirements = useQuery({ queryKey: legalKeys.requirements, queryFn: fetchRequirements });

  const change = useMutation({
    mutationFn: ({ row, action }: Pending) =>
      api.post<RequirementRow>(`/legal/requirements/${row.public_id}/${action}`),
    onSuccess: async (row, { action }) => {
      await queryClient.invalidateQueries({ queryKey: legalKeys.all });
      toast.success(
        action === 'activate' ? `${row.title} is active` : `${row.title} retired`,
        action === 'activate'
          ? 'Clients it applies to are asked on their next screen.'
          : 'Clients are no longer asked. Their decisions stay on record.',
      );
    },
    onError: (error) =>
      toast.error('Could not change it', error instanceof ApiError ? error.message : undefined),
  });

  const rows = requirements.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Consent requirements"
        description="What clients are asked to accept or consent to. Mandatory ones block the app until accepted."
        breadcrumbs={[{ label: 'Legal' }, { label: 'Consent requirements' }]}
        actions={
          <Can permission="core.consent_requirement.create">
            <Button onClick={() => setEditing('new')}>
              <Plus className="size-4" />
              New requirement
            </Button>
          </Can>
        }
      />

      <Card>
        {requirements.isLoading ? (
          <LoadingState label="Loading requirements" />
        ) : requirements.isError ? (
          <ErrorState
            message={requirements.error instanceof ApiError ? requirements.error.message : undefined}
            onRetry={() => void requirements.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ListChecks className="size-5" />}
            title="Nothing is asked of clients"
            description="Publish a policy first, then create a requirement that presents it and activate it."
          />
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((row) => (
              <li key={row.public_id} className="space-y-1.5 px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 space-y-0.5">
                    <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-heading">
                      {row.title}
                      <StatusChip value={row.status} tones={STATUS_TONES} />
                      <Badge tone={row.mandatory ? 'danger' : 'neutral'}>
                        {row.mandatory ? 'Mandatory' : 'Optional'}
                      </Badge>
                      <Badge tone="neutral">{labelOf(options.data?.requirement_kinds, row.kind)}</Badge>
                      {row.withdrawable ? <Badge tone="info">Withdrawable</Badge> : null}
                      {row.enforce_on_api ? <Badge tone="warning">API keys too</Badge> : null}
                    </p>
                    <p className="text-2xs text-faint">
                      {[
                        `Presents ${row.document_title}`,
                        row.service
                          ? `${labelOf(options.data?.services, row.service)} clients`
                          : 'Every client',
                        row.channel ? labelOf(options.data?.channels, row.channel) : null,
                        row.purpose ? labelOf(options.data?.purposes, row.purpose) : null,
                        row.effective_at ? `from ${formatDateTime(row.effective_at)}` : null,
                        row.status === 'active' ? `${formatNumber(row.accepted)} accepted` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <Can permission="core.consent_requirement.edit">
                    <div className="flex items-center gap-1.5">
                      {row.status !== 'retired' ? (
                        <Button variant="secondary" size="sm" onClick={() => setEditing(row)}>
                          <Pencil className="size-3.5" />
                          Edit
                        </Button>
                      ) : null}
                      {row.status === 'draft' ? (
                        <Button size="sm" onClick={() => setPending({ row, action: 'activate' })}>
                          <CheckCircle2 className="size-3.5" />
                          Activate
                        </Button>
                      ) : null}
                      {row.status === 'active' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setPending({ row, action: 'retire' })}
                        >
                          <Ban className="size-3.5" />
                          Retire
                        </Button>
                      ) : null}
                    </div>
                  </Can>
                </div>
                <p className="rounded-control bg-surface-2 px-2.5 py-1.5 text-xs text-body">
                  {row.statement}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <RequirementFormDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        requirement={editing === 'new' ? null : editing}
        documents={documents.data ?? []}
        options={options.data}
      />
      <Confirm
        open={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={() => (pending ? change.mutateAsync(pending).then(() => undefined) : undefined)}
        title={
          pending?.action === 'activate' ? `Activate ${pending.row.title}?` : `Retire ${pending?.row.title ?? ''}?`
        }
        message={
          pending?.action === 'activate'
            ? pending.row.mandatory
              ? 'Every client it applies to must accept it before they can use the app again. API keys are held back too if that is switched on.'
              : 'Clients it applies to are asked. They may decline and keep using the app.'
            : 'Clients are no longer asked, and a mandatory one stops blocking them. Every decision stays on record.'
        }
        confirmLabel={pending?.action === 'activate' ? 'Activate' : 'Retire'}
        variant={pending?.action === 'activate' ? 'primary' : 'danger'}
      />
    </div>
  );
}
